import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, UserPlus, ShoppingCart, Eye } from "lucide-react";
import { LeadFollowUpCard } from "./LeadFollowUpCard";
import { askLeave } from "@/lib/navGuard";
import { fmtDateTime } from "@/lib/dateFormat";

const STATUS_COLORS = { open: "bg-blue-50 text-blue-700 border-blue-200", handed_off: "bg-amber-50 text-amber-700 border-amber-200", converted: "bg-emerald-50 text-emerald-600 border-emerald-200", parked: "bg-zinc-50 text-zinc-500 border-zinc-200", closed_won: "bg-emerald-50 text-emerald-600 border-emerald-200", closed_lost: "bg-red-50 text-red-600 border-red-200" };
const HANDOFF_STATUS_COLORS = { sent: "text-blue-600 border-blue-200", under_review: "text-amber-600 border-amber-200", accepted: "text-emerald-600 border-emerald-200", declined: "text-red-600 border-red-200", fulfilled: "text-emerald-700 border-emerald-300", not_fulfilled: "text-red-700 border-red-300", withdrawn: "text-zinc-500 border-zinc-200" };

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { getValues, getItems } = useConfig();
  const buyerTypes = getValues("lead_buyer_types");
  const purposes = getValues("lead_purposes");
  // L3 — show the configured label (walk_in → "Walk-in")
  const sourceLabel = (key) => ((getItems("lead_sources") || []).find(i => i.key === key)?.value) || key || "—";
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [dpOptions, setDpOptions] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedDp, setSelectedDp] = useState("");
  const [remarks, setRemarks] = useState("");
  const [channels, setChannels] = useState([]);
  const [showConvert, setShowConvert] = useState(false);
  const [convertForm, setConvertForm] = useState({ type: "General Trade", gst_number: "", gst_not_available: false, channel_id: "", billing_address: { line1: "", city: "", state: "", pincode: "" } });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [skus, setSkus] = useState([]);
  const [previewSeen, setPreviewSeen] = useState(false);
  const [dpError, setDpError] = useState("");
  const [showConsentConfirm, setShowConsentConfirm] = useState(false);
  const [pendingSend, setPendingSend] = useState(null);
  const editFromUrl = useRef(false);
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin","admin"].includes(r)) || perms.includes("leads.admin");
  const canRoute = isAdmin || perms.includes("leads.route");
  const canEdit = isAdmin || perms.includes("leads.edit");
  const canConvert = isAdmin || perms.includes("leads.convert");
  // PART C (C5): hand the lead to another agent from the detail page
  const [agents, setAgents] = useState([]);
  const [assignForm, setAssignForm] = useState({ open: false, user_id: "", reason: "" });
  // LEAD DESK BUILD item 2(e) — move the stage by hand; Lost always needs a reason
  const [stageForm, setStageForm] = useState({ open: false, stage: "", reason: "" });
  const canEditLead = isAdmin || perms.includes("leads.edit");
  // LEAD DESK FIX-UP (Fix 4b) — a Lost lead is brought back with a compulsory reason
  const [reopenForm, setReopenForm] = useState({ open: false, reason: "" });
  const reopenLead = async () => {
    if (!reopenForm.reason.trim()) { toast.error("Give a reason for reopening this lead"); return; }
    try {
      const { data } = await api.post(`/leads/${leadId}/reopen`, { reason: reopenForm.reason.trim() });
      toast.success(data.message);
      setReopenForm({ open: false, reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reopen the lead"); }
  };
  const moveStage = async () => {
    if (stageForm.stage === "lost" && !stageForm.reason.trim()) { toast.error("Pick why this lead is lost"); return; }
    try {
      const { data } = await api.put(`/leads/${leadId}/stage`,
        { stage: stageForm.stage, lost_reason: stageForm.reason.trim() });
      toast.success(data.message);
      setStageForm({ open: false, stage: "", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not move the lead"); }
  };
  useEffect(() => {
    if (!canRoute) return;
    api.get("/leads/assignable-agents").then(r => setAgents(r.data?.agents || [])).catch(() => {});
  }, [canRoute]);
  const reassign = async (userId) => {
    // PART C FIX-UP (Fix 5a): a reason is compulsory, so the dialog asks before the call
    setAssignForm({ open: true, user_id: userId === "none" ? "" : userId, reason: "" });
  };

  const submitReassign = async () => {
    if (!assignForm.reason.trim()) { toast.error("Please give a reason for moving this lead"); return; }
    try {
      const { data } = await api.put(`/leads/${leadId}/assign`,
        { assign_to: assignForm.user_id, reason: assignForm.reason.trim() });
      toast.success(data.message);
      setAssignForm({ open: false, user_id: "", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not move the lead"); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/leads/${leadId}`)
      .then(r => {
        setLead(r.data);
        if (r.data.party_id) api.get(`/leads/${leadId}/timeline`).then(a => setActivities(a.data || [])).catch(() => {});
      })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
    api.get("/leads/channels").then(r => setChannels(r.data || [])).catch(() => {});
    api.get("/catalogue/skus").then(r => setSkus((r.data || []).filter(s => s.status !== "Draft"))).catch(() => toast.error("Failed to load SKUs"));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (lead && searchParams.get("edit") === "1" && !editFromUrl.current) { editFromUrl.current = true; startEdit(); } }, [lead]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDpOptions = () => {
    setDpError("");
    api.get(`/leads/${leadId}/dp-options`)
      .then(r => { setDpOptions(r.data); setDpError(""); })
      .catch(e => { const msg = e.response?.data?.detail || "Failed to load DP options"; setDpError(msg); toast.error(msg); });
  };

  const loadPreview = (dpId) => {
    setSelectedDp(dpId);
    api.get(`/leads/${leadId}/handoff-preview?dp_profile_id=${dpId}`)
      .then(r => { setPreview(r.data); setPreviewSeen(true); })
      .catch(e => toast.error(e.response?.data?.detail || "Preview failed"));
  };

  const sendHandoff = async () => {
    try {
      const r = await api.post(`/leads/${leadId}/handoffs`, { dp_profile_id: selectedDp, remarks });
      toast.success("Handoff sent");
      if (r.data.consent_warning) toast.warning(r.data.consent_warning);
      setPreview(null); setSelectedDp(""); setRemarks(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Handoff failed"); }
  };

  const updateHandoffStatus = async (hId, status, note = "") => {
    try {
      await api.put(`/leads/handoffs/${hId}/status`, { status, note });
      toast.success(`Handoff ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleConvert = async () => {
    try {
      const payload = { ...convertForm, name: lead?.name, company_name: lead?.company, phones: lead?.phones, email: lead?.email, state: lead?.state, credit_limit: 0, credit_days: 0 };
      if (!payload.billing_address.line1) payload.billing_address.line1 = lead?.address || "";
      if (!payload.billing_address.city) payload.billing_address.city = lead?.city || "";
      if (!payload.billing_address.state) payload.billing_address.state = lead?.state || "";
      if (!payload.billing_address.pincode) payload.billing_address.pincode = lead?.pincode || "";
      const r = await api.post(`/leads/${leadId}/convert-customer`, payload);
      toast.success(`Customer ${r.data.action}: ${r.data.oms_customer_id}`);
      setShowConvert(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Conversion failed"); }
  };

  const startDpApp = async () => {
    try {
      const r = await api.post(`/leads/${leadId}/start-dp-application`);
      toast.success(`DP application ${r.data.action}: ${r.data.dp_profile_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = () => {
    setEditForm({
      lead_kind: lead.lead_kind || "buyer",
      sales_channel_ids: lead.sales_channel_ids || [],
      buyer_type: lead.buyer_type || "",
      source: lead.source || "",
      pincode: lead.pincode || "",
      address: lead.address || "",
      city: lead.city || "",
      district: lead.district || "",
      state: lead.state || "",
      requirement: lead.requirement || { items: [], expected_value_paise: 0, purpose: "", deliver_by: "", remarks_for_dp: "" },
      internal_notes: lead.internal_notes || "",
      consent_share_with_partner: lead.consent_share_with_partner?.value || false,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (typeof payload.requirement?.expected_value_paise === "string") {
        payload.requirement.expected_value_paise = Math.round(parseFloat(payload.requirement.expected_value_paise || "0") * 100);
      }
      await api.put(`/leads/${leadId}`, payload);
      toast.success("Lead saved");
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const addSkuLine = () => {
    const req = { ...editForm.requirement };
    req.items = [...(req.items || []), { sku_id: "", sku_name: "", qty: 1 }];
    setEditForm({ ...editForm, requirement: req });
  };

  const removeSkuLine = (idx) => {
    const req = { ...editForm.requirement };
    req.items = req.items.filter((_, i) => i !== idx);
    setEditForm({ ...editForm, requirement: req });
  };

  const attemptSendHandoff = () => {
    const consent = lead?.consent_share_with_partner?.value;
    if (!consent) {
      setShowConsentConfirm(true);
      setPendingSend(true);
    } else {
      sendHandoff();
    }
  };

  const confirmConsentSend = () => {
    setShowConsentConfirm(false);
    setPendingSend(null);
    sendHandoff();
  };

  if (loading || !lead) return <div className="p-6 text-xs text-zinc-400">Loading...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="lead-detail-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => askLeave(() => navigate("/leads"))} data-testid="lead-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">{lead.name}</h1>
            <p className="text-xs text-zinc-400">{lead.company} · {(lead.phones || [])[0]} · {lead.pincode}</p>
          </div>
          <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLORS[lead.status] || ""}`} data-testid="lead-status">{lead.status?.replace(/_/g, " ")}</Badge>
          <Badge variant="outline" className="text-[10px] capitalize">{lead.lead_kind}</Badge>
        </div>
        <div className="flex gap-2">
          {canEditLead && lead.status === "lost" && (
            <Button size="sm" variant="outline" className="h-8 text-[11px] rounded-sm border-amber-300 text-amber-700"
              onClick={() => setReopenForm({ open: true, reason: "" })} data-testid="lead-reopen-btn">Reopen</Button>
          )}
          {reopenForm.open && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="lead-reopen-dialog">
              <div className="bg-white rounded-sm p-4 w-[420px] border border-zinc-200">
                <p className="text-sm font-semibold text-zinc-900">Reopen this lead</p>
                <p className="text-xs text-zinc-500 mt-0.5">It goes back to the stage it was at before it was lost.</p>
                <Label className="text-[10px] text-zinc-500 uppercase mt-3 block">Reason (required)</Label>
                <Input value={reopenForm.reason} autoFocus
                  onChange={e => setReopenForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Asked us to call again next week…"
                  className="h-8 text-xs mt-1 rounded-sm" data-testid="lead-reopen-reason" />
                <div className="flex gap-2 mt-3 justify-end">
                  <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                    onClick={() => setReopenForm({ open: false, reason: "" })}
                    data-testid="lead-reopen-cancel">Cancel</Button>
                  <Button size="sm" className="rounded-sm h-8 text-xs" onClick={reopenLead}
                    data-testid="lead-reopen-save">Reopen the lead</Button>
                </div>
              </div>
            </div>
          )}
          {canEditLead && lead.status !== "lost" && (
            <div className="flex gap-1 items-center" data-testid="lead-stage-control">
              <span className="text-[10px] uppercase text-zinc-400">Move to</span>
              {[["contacted", "Contacted"], ["qualified", "Qualified"], ["lost", "Lost"]].map(([k, l]) => (
                <Button key={k} size="sm" variant="outline" className="h-8 text-[11px] rounded-sm"
                  onClick={() => (k === "lost"
                    ? setStageForm({ open: true, stage: "lost", reason: "" })
                    : setStageForm({ open: true, stage: k, reason: "" }))}
                  data-testid={`lead-stage-${k}`}>{l}</Button>
              ))}
            </div>
          )}
          {stageForm.open && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="lead-stage-dialog">
              <div className="bg-white rounded-sm p-4 w-[420px] border border-zinc-200">
                <p className="text-sm font-semibold text-zinc-900">Move this lead to {stageForm.stage}</p>
                {stageForm.stage === "lost" && (
                  <>
                    <Label className="text-[10px] text-zinc-500 uppercase mt-3 block">Why is it lost (required)</Label>
                    <Input value={stageForm.reason} autoFocus
                      onChange={e => setStageForm(f => ({ ...f, reason: e.target.value }))}
                      placeholder="Not interested, Price too high, Out of area…"
                      className="h-8 text-xs mt-1 rounded-sm" data-testid="lead-stage-reason" />
                  </>
                )}
                <div className="flex gap-2 mt-3 justify-end">
                  <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                    onClick={() => setStageForm({ open: false, stage: "", reason: "" })}
                    data-testid="lead-stage-cancel">Cancel</Button>
                  <Button size="sm" className="rounded-sm h-8 text-xs" onClick={moveStage}
                    data-testid="lead-stage-save">Move the lead</Button>
                </div>
              </div>
            </div>
          )}
          {canRoute && (
            <Select value={lead.owner_user_id || "none"} onValueChange={reassign}>
              <SelectTrigger className="h-8 text-xs w-[220px] rounded-sm" data-testid="lead-assign-select">
                <SelectValue placeholder="Assign to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {agents.map(a => <SelectItem key={a.user_id} value={a.user_id} data-testid={`lead-assign-${a.user_id}`}>{a.name}{a.is_me ? " (you)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {assignForm.open && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="lead-assign-dialog">
              <div className="bg-white rounded-sm p-4 w-[420px] border border-zinc-200">
                <p className="text-sm font-semibold text-zinc-900">Move this lead</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  To {agents.find(a => a.user_id === assignForm.user_id)?.name || "nobody (unassigned)"}
                </p>
                <Label className="text-[10px] text-zinc-500 uppercase mt-3 block">Reason (required)</Label>
                <Input value={assignForm.reason} autoFocus
                  onChange={e => setAssignForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Why is this lead changing hands?"
                  className="h-8 text-xs mt-1 rounded-sm" data-testid="lead-assign-reason" />
                <p className="text-[10px] text-zinc-400 mt-1">Both the new and the previous owner are told.</p>
                <div className="flex gap-2 mt-3 justify-end">
                  <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                    onClick={() => setAssignForm({ open: false, user_id: "", reason: "" })}
                    data-testid="lead-assign-cancel">Cancel</Button>
                  <Button size="sm" className="rounded-sm h-8 text-xs" onClick={submitReassign}
                    data-testid="lead-assign-save">Move the lead</Button>
                </div>
              </div>
            </div>
          )}
          {canRoute && lead.lead_kind !== "buyer" && <Button size="sm" variant="outline" onClick={startDpApp} data-testid="start-dp-btn"><UserPlus className="w-3.5 h-3.5 mr-1" /> Start DP Application</Button>}
          {canConvert && <Button size="sm" variant="outline" onClick={() => { if (!showConvert) setConvertForm(f => ({...f, billing_address: { line1: lead?.address || "", city: lead?.city || "", state: lead?.state || "", pincode: lead?.pincode || "" }})); setShowConvert(!showConvert); }} data-testid="convert-btn"><ShoppingCart className="w-3.5 h-3.5 mr-1" /> Convert to Customer</Button>}
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-200 mb-4">
        {["details", "routing", "handoffs", "timeline"].map(t => (
          <button key={t} onClick={() => { setTab(t); if (t === "routing") loadDpOptions(); }} className={`px-3 py-1.5 text-xs font-medium border-b-2 capitalize ${tab === t ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400"}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "details" && !editing && (
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-zinc-200 rounded-sm p-4 space-y-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-zinc-500 uppercase">Contact</h3>
              {canEdit && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={startEdit} data-testid="edit-lead-btn">Edit</Button>}
            </div>
            <Row label="Name" value={lead.name} /><Row label="Company" value={lead.company} />
            <Row label="Phone" value={(lead.phones || []).join(", ")} /><Row label="Email" value={lead.email} />
            <Row label="Address" value={lead.address} />
            <Row label="City" value={lead.city} />
            <Row label="District" value={lead.district} />
            <Row label="State" value={lead.state} />
            <Row label="Pincode" value={lead.pincode} mono /><Row label="Source" value={sourceLabel(lead.source)} />
          </div>
          <div className="border border-zinc-200 rounded-sm p-4 space-y-2">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase mb-2">Requirement</h3>
            <Row label="Buyer Type" value={lead.buyer_type ? lead.buyer_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "—"} />
            <Row label="Purpose" value={lead.requirement?.purpose ? lead.requirement.purpose.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "—"} />
            <Row label="Deliver By" value={lead.requirement?.deliver_by} />
            <Row label="Value" value={lead.requirement?.expected_value_paise ? `₹ ${(lead.requirement.expected_value_paise / 100).toLocaleString()}` : "—"} />
            {(lead.requirement?.items || []).length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] text-zinc-400 font-medium mb-1">Items</p>
                {lead.requirement.items.map((it, i) => <p key={i} className="text-xs text-zinc-600">{it.sku_name} x {it.qty}</p>)}
              </div>
            )}
            <div className="mt-3 pt-2 border-t border-zinc-100">
              <p className="text-[10px] text-red-400 font-medium">Internal Notes (never shared with DP)</p>
              <p className="text-xs text-zinc-600 mt-1">{lead.internal_notes || "—"}</p>
            </div>
          </div>
          <LeadFollowUpCard leadId={leadId} canEdit={canEdit} onChanged={load} />
          {lead.marketing_source && (
            <div className="col-span-2 border border-blue-200 bg-blue-50/60 rounded-sm p-4" data-testid="marketing-source-card">
              <h3 className="text-xs font-semibold text-blue-900 uppercase mb-2">Marketing source</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
                <Row label="Campaign" value={lead.marketing_source.campaign} />
                <Row label="Ad set" value={lead.marketing_source.ad_set} />
                <Row label="Ad name" value={lead.marketing_source.ad_name} />
                <Row label="Form name" value={lead.marketing_source.form_name} />
                <Row label="Lead date" value={lead.marketing_source.lead_date} />
                <Row label="Source file" value={lead.marketing_source.source_file} />
              </div>
              <p className="text-[10px] text-blue-500 mt-2">Captured at import — not editable.</p>
            </div>
          )}
          {lead.outcomes?.oms_customer_id && (
            <div className="col-span-2 border border-emerald-200 bg-emerald-50 rounded-sm p-3">
              <p className="text-xs text-emerald-700">Converted to OMS Customer: <span className="font-mono">{lead.outcomes.oms_customer_id}</span></p>
            </div>
          )}
          {(lead.outcomes?.dp_profile_ids || []).length > 0 && (
            <div className="col-span-2 border border-violet-200 bg-violet-50 rounded-sm p-3">
              <p className="text-xs text-violet-700">DP Applications: {lead.outcomes.dp_profile_ids.join(", ")}</p>
            </div>
          )}
        </div>
      )}

      {tab === "details" && editing && (
        <div className="space-y-4" data-testid="edit-mode">
          <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase mb-2">Classification</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Kind</Label>
                <Select value={editForm.lead_kind} onValueChange={v => setEditForm({...editForm, lead_kind: v})}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="edit-kind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="buyer">Buyer</SelectItem><SelectItem value="partner">Partner</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Buyer Type</Label>
                <Select value={editForm.buyer_type} onValueChange={v => setEditForm({...editForm, buyer_type: v})}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="edit-buyer-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{buyerTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Source</Label><Input value={editForm.source} onChange={e => setEditForm({...editForm, source: e.target.value})} className="h-8 text-xs mt-1" /></div>
            </div>
            {channels.length > 0 && (
              <div><Label className="text-xs">Sales Channels</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {channels.map(c => (<button key={c._id} onClick={() => setEditForm({...editForm, sales_channel_ids: editForm.sales_channel_ids.includes(c._id) ? editForm.sales_channel_ids.filter(x => x !== c._id) : [...editForm.sales_channel_ids, c._id]})} className={`px-2 py-0.5 text-[10px] rounded-sm border ${editForm.sales_channel_ids.includes(c._id) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`} data-testid={`edit-ch-${c._id}`}>{c.name}</button>))}
                </div>
              </div>
            )}
          </div>
          <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase mb-2">Location</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Pincode</Label><Input value={editForm.pincode} onChange={e => setEditForm({...editForm, pincode: e.target.value})} className="h-8 text-xs mt-1" maxLength={6} data-testid="edit-pincode" /></div>
              <div><Label className="text-xs">City</Label><Input value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})} className="h-8 text-xs mt-1" data-testid="edit-city" /></div>
              <div><Label className="text-xs">District</Label><Input value={editForm.district} onChange={e => setEditForm({...editForm, district: e.target.value})} className="h-8 text-xs mt-1" data-testid="edit-district" /></div>
              <div><Label className="text-xs">State</Label><Input value={editForm.state} onChange={e => setEditForm({...editForm, state: e.target.value})} className="h-8 text-xs mt-1" data-testid="edit-state" /></div>
            </div>
            <div><Label className="text-xs">Address</Label><Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} className="h-8 text-xs mt-1" /></div>
          </div>
          <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-zinc-500 uppercase">Requirement</h3>
              <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={addSkuLine} data-testid="add-sku-btn">+ Add SKU</Button>
            </div>
            {(editForm.requirement?.items || []).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Select value={item.sku_id} onValueChange={v => { const s = skus.find(sk => sk._id === v); const req = {...editForm.requirement}; req.items = [...req.items]; req.items[idx] = {...req.items[idx], sku_id: v, sku_name: s?.sku_name || ""}; setEditForm({...editForm, requirement: req}); }}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid={`sku-select-${idx}`}><SelectValue placeholder="Select SKU" /></SelectTrigger>
                  <SelectContent>{skus.map(s => <SelectItem key={s._id} value={s._id}>{s.sku_name} ({s.sku_id})</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" value={item.qty} onChange={e => { const req = {...editForm.requirement}; req.items = [...req.items]; req.items[idx] = {...req.items[idx], qty: parseInt(e.target.value) || 0}; setEditForm({...editForm, requirement: req}); }} className="h-8 text-xs w-20" placeholder="Qty" data-testid={`sku-qty-${idx}`} />
                <button onClick={() => removeSkuLine(idx)} className="text-red-400 text-xs">x</button>
              </div>
            ))}
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Value (₹)</Label><Input type="number" value={(editForm.requirement?.expected_value_paise || 0) / 100} onChange={e => setEditForm({...editForm, requirement: {...editForm.requirement, expected_value_paise: Math.round((parseFloat(e.target.value) || 0) * 100)}})} className="h-8 text-xs mt-1" data-testid="edit-value" /></div>
              <div><Label className="text-xs">Purpose</Label>
                <Select value={editForm.requirement?.purpose || ""} onValueChange={v => setEditForm({...editForm, requirement: {...editForm.requirement, purpose: v}})}>
                  <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{purposes.map(p => <SelectItem key={p} value={p}>{p.replace(/_/g," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Deliver By</Label><Input type="date" value={editForm.requirement?.deliver_by || ""} onChange={e => setEditForm({...editForm, requirement: {...editForm.requirement, deliver_by: e.target.value}})} className="h-8 text-xs mt-1" data-testid="edit-deliver-by" /></div>
            </div>
            <div><Label className="text-xs">Remarks for DP</Label><Input value={editForm.requirement?.remarks_for_dp || ""} onChange={e => setEditForm({...editForm, requirement: {...editForm.requirement, remarks_for_dp: e.target.value}})} className="h-8 text-xs mt-1" /></div>
          </div>
          <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
            <h3 className="text-xs font-semibold text-zinc-500 uppercase mb-2"><span className="text-red-500">Internal Notes</span> <span className="text-red-400 font-normal">(never shared with DP)</span></h3>
            <Input value={editForm.internal_notes} onChange={e => setEditForm({...editForm, internal_notes: e.target.value})} className="h-8 text-xs" data-testid="edit-notes" />
            <label className="flex items-center gap-2 text-xs text-zinc-600 mt-2">
              <input type="checkbox" checked={editForm.consent_share_with_partner || false} onChange={e => setEditForm({...editForm, consent_share_with_partner: e.target.checked})} className="rounded border-zinc-300" data-testid="edit-consent" />
              Lead agreed their details may be shared with an authorised SKIPPI partner for delivery
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" onClick={saveEdit} data-testid="save-edit-btn">Save Changes</Button>
          </div>
        </div>
      )}

      {tab === "routing" && (
        <div data-testid="routing-panel">
          {dpError ? (
            <div className="border border-red-200 bg-red-50 rounded-sm p-4" data-testid="routing-error">
              <p className="text-sm text-red-700 mb-2">{dpError}</p>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => { setTab("details"); startEdit(); }} data-testid="routing-edit-link">Edit Lead Pincode</Button>
            </div>
          ) : !dpOptions ? <p className="text-xs text-zinc-400">Loading DP options...</p> : (
            <>
              <p className="text-xs text-zinc-500 mb-2">Pincode: <span className="font-mono font-medium">{dpOptions.pincode}</span> — {dpOptions.dp_options?.length} DPs</p>
              {(dpOptions.restricted_brands || []).length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-md p-2 mb-2" data-testid="restricted-brands-warning">
                  <p className="text-[11px] font-medium text-amber-700 mb-0.5">These brands cannot be sold here:</p>
                  {dpOptions.restricted_brands.map((rb, i) => (
                    <p key={i} className="text-[11px] text-amber-700">- {rb.brand_name} — {rb.reason}</p>
                  ))}
                </div>
              )}
              <div className="space-y-2 mb-4">
                {(dpOptions.dp_options || []).map(dp => (
                  <div key={dp.profile_id} className={`flex items-center justify-between border rounded-sm px-3 py-2 ${dp.outside_territory ? "border-amber-200 bg-amber-50" : "border-zinc-200"}`} data-testid={`dp-option-${dp.profile_id}`}>
                    <div>
                      <span className="text-xs font-medium text-zinc-800">{dp.business_name}</span>
                      {dp.outside_territory && <Badge variant="outline" className="ml-1.5 text-[9px] text-amber-600 border-amber-300">outside territory</Badge>}
                      {dp.brands_for_pincode.length > 0 && <span className="text-[10px] text-zinc-500 ml-2">{dp.brands_for_pincode.join(", ")}</span>}
                    </div>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => loadPreview(dp.profile_id)} data-testid={`preview-${dp.profile_id}`}><Eye className="w-3 h-3 mr-1" /> Preview</Button>
                  </div>
                ))}
              </div>
              {preview && (
                <div className="border border-blue-200 bg-blue-50 rounded-sm p-4 mb-4" data-testid="handoff-preview">
                  <h3 className="text-xs font-semibold text-blue-800 mb-2">Handoff Preview</h3>
                  <p className="text-[10px] text-zinc-500 mb-1">Subject: {preview.subject}</p>
                  <div className="bg-white border border-blue-100 rounded-sm p-3 text-xs overflow-y-auto" dangerouslySetInnerHTML={{ __html: preview.html_body }} />
                  <div className="mt-3">
                    <Label className="text-xs text-zinc-500">Agent Remarks</Label>
                    <Input value={remarks} onChange={e => setRemarks(e.target.value)} className="h-8 text-xs mt-1" placeholder="Any notes for the DP..." data-testid="handoff-remarks" />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={attemptSendHandoff} disabled={!previewSeen} data-testid="send-handoff-btn"><Send className="w-3.5 h-3.5 mr-1" /> Send Handoff</Button>
                    <Button size="sm" variant="outline" onClick={() => { setPreview(null); setSelectedDp(""); setPreviewSeen(false); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "handoffs" && (
        <div data-testid="handoffs-section">
          {(lead.handoffs || []).length === 0 ? <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No handoffs yet.</p> : (
            <div className="space-y-3">
              {lead.handoffs.map(h => (
                <div key={h._id} className="border border-zinc-200 rounded-sm p-4" data-testid={`handoff-${h._id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-800">{h.dp_business_name}</span>
                      <Badge variant="outline" className={`text-[10px] capitalize ${HANDOFF_STATUS_COLORS[h.status] || ""}`}>{h.status?.replace(/_/g, " ")}</Badge>
                      {h.outside_territory && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300">outside territory</Badge>}
                    </div>
                    <span className="text-[10px] text-zinc-400">{fmtDateTime(h.sent_at)}</span>
                  </div>
                  {(h.status_history || []).map((sh, i) => (
                    <p key={i} className="text-[10px] text-zinc-500">{sh.status} {sh.note ? `— ${sh.note}` : ""} · {fmtDateTime(sh.at)}</p>
                  ))}
                  {canRoute && ["sent", "under_review", "accepted"].includes(h.status) && (
                    <div className="flex gap-1.5 mt-2">
                      {h.status === "sent" && <><Btn onClick={() => updateHandoffStatus(h._id, "under_review")} label="Under Review" tid={`ur-${h._id}`} /><Btn onClick={() => updateHandoffStatus(h._id, "accepted")} label="Accepted" c="bg-emerald-600 text-white" tid={`acc-${h._id}`} /><Btn onClick={() => updateHandoffStatus(h._id, "declined")} label="Declined" c="text-red-600" tid={`dec-${h._id}`} /><Btn onClick={() => updateHandoffStatus(h._id, "withdrawn")} label="Withdraw" c="text-zinc-500" tid={`wd-${h._id}`} /></>}
                      {h.status === "under_review" && <><Btn onClick={() => updateHandoffStatus(h._id, "accepted")} label="Accepted" c="bg-emerald-600 text-white" tid={`acc-${h._id}`} /><Btn onClick={() => updateHandoffStatus(h._id, "declined")} label="Declined" c="text-red-600" tid={`dec-${h._id}`} /><Btn onClick={() => updateHandoffStatus(h._id, "withdrawn")} label="Withdraw" c="text-zinc-500" tid={`wd-${h._id}`} /></>}
                      {h.status === "accepted" && <><Btn onClick={() => updateHandoffStatus(h._id, "fulfilled")} label="Fulfilled" c="bg-emerald-600 text-white" tid={`ful-${h._id}`} /><Btn onClick={() => updateHandoffStatus(h._id, "not_fulfilled")} label="Not Fulfilled" c="text-red-600" tid={`nf-${h._id}`} /></>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "timeline" && (
        <div data-testid="timeline-section">
          {activities.length === 0 ? <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No activity.</p> : (
            <div className="space-y-2">{activities.map((a, i) => (
              <div key={a._id || i} className="border-l-2 border-zinc-200 pl-3 py-1">
                <p className="text-xs text-zinc-800">{a.summary || a.type}</p>
                <p className="text-[10px] text-zinc-400">{a.outcome && <span className="capitalize">{a.outcome} · </span>}{fmtDateTime(a.occurred_at || a.created_at)}</p>
              </div>
            ))}</div>
          )}
        </div>
      )}

      {showConvert && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowConvert(false)}>
          <div className="bg-white rounded-sm p-6 w-full max-w-lg shadow-lg" onClick={e => e.stopPropagation()} data-testid="convert-form">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4">Convert to OMS Customer</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Customer Type</Label><Input value={convertForm.type} onChange={e => setConvertForm({...convertForm, type: e.target.value})} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-xs">Channel</Label>
                  <Select value={convertForm.channel_id} onValueChange={v => setConvertForm({...convertForm, channel_id: v})}>
                    <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{channels.map(c => <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">GST Number</Label>
                <Input value={convertForm.gst_number} onChange={e => setConvertForm({...convertForm, gst_number: e.target.value})} className="h-8 text-xs mt-1" disabled={convertForm.gst_not_available} data-testid="convert-gst-input" />
                <label className="flex items-center gap-1.5 mt-1 text-xs text-zinc-500">
                  <input type="checkbox" checked={convertForm.gst_not_available} onChange={e => setConvertForm({...convertForm, gst_not_available: e.target.checked, gst_number: e.target.checked ? "" : convertForm.gst_number})} className="rounded border-zinc-300" data-testid="convert-gst-na-checkbox" />
                  No GST number available
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Billing Address</Label><Input value={convertForm.billing_address.line1} onChange={e => setConvertForm({...convertForm, billing_address: {...convertForm.billing_address, line1: e.target.value}})} className="h-8 text-xs mt-1" placeholder="Line 1" /></div>
                <div><Label className="text-xs">City</Label><Input value={convertForm.billing_address.city} onChange={e => setConvertForm({...convertForm, billing_address: {...convertForm.billing_address, city: e.target.value}})} className="h-8 text-xs mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">State</Label><Input value={convertForm.billing_address.state} onChange={e => setConvertForm({...convertForm, billing_address: {...convertForm.billing_address, state: e.target.value}})} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-xs">Pincode</Label><Input value={convertForm.billing_address.pincode} onChange={e => setConvertForm({...convertForm, billing_address: {...convertForm.billing_address, pincode: e.target.value}})} className="h-8 text-xs mt-1" maxLength={6} /></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowConvert(false)}>Cancel</Button>
                <Button size="sm" onClick={handleConvert} data-testid="confirm-convert-btn">Convert</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showConsentConfirm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowConsentConfirm(false)}>
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-lg" onClick={e => e.stopPropagation()} data-testid="consent-confirm-dialog">
            <h2 className="text-lg font-semibold text-amber-800 mb-2">Consent Not Recorded</h2>
            <p className="text-sm text-zinc-600 mb-4">This lead has not agreed to sharing with a partner — send anyway?</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowConsentConfirm(false)}>Cancel</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={confirmConsentSend} data-testid="confirm-consent-send">Send Anyway</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return <div className="flex justify-between text-xs"><span className="text-zinc-400">{label}</span><span className={`text-zinc-700 text-right ${mono ? "font-mono" : ""}`}>{value || "—"}</span></div>;
}

function Btn({ onClick, label, c = "", tid }) {
  return <button onClick={onClick} className={`px-2 py-0.5 text-[10px] rounded-sm border border-zinc-200 ${c}`} data-testid={tid}>{label}</button>;
}
